import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Palette, Type, ListOrdered, Save, RotateCcw, Check, ChevronUp, ChevronDown, ExternalLink, Film } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { id: "caramel", label: "Caramel (signature)", colors: { accent: "#C68E59", bg: "#FAF3EA", dark: "#1A1A1A" } },
  { id: "noir",    label: "Noir éditorial",       colors: { accent: "#E6C29A", bg: "#0F0F0F", dark: "#000000" } },
  { id: "rose",    label: "Rose poudré",          colors: { accent: "#B86F6F", bg: "#FBEFEC", dark: "#2A1717" } },
  { id: "ivoire",  label: "Ivoire chaud",         colors: { accent: "#A26F3E", bg: "#F8F4EA", dark: "#23211B" } },
  { id: "olive",   label: "Olive nature",         colors: { accent: "#7C8B5E", bg: "#F2EFE3", dark: "#1F2317" } },
  { id: "custom",  label: "Personnalisé",         colors: null },
];

const FONTS = [
  { value: "Cormorant Garamond", label: "Cormorant Garamond — Élégance intemporelle" },
  { value: "Playfair Display",   label: "Playfair Display — Magazine sophistiqué" },
  { value: "DM Serif Display",   label: "DM Serif Display — Affirmé contemporain" },
  { value: "Italiana",           label: "Italiana — Couture Vogue italienne" },
  { value: "Cardo",              label: "Cardo — Calligraphie classique" },
];

const SECTIONS_DEFINITION = [
  { id: "univers",        label: "Univers",        description: "Mood, palette, inspirations, citation signature" },
  { id: "portfolio",      label: "Portfolio",      description: "Vos photos en grille masonry premium" },
  { id: "profil",         label: "Profil",         description: "Carte d'identité (taille, mensurations, expérience)" },
  { id: "collaborations", label: "Collaborations", description: "Vos expériences passées avec crédits" },
  { id: "helencia",       label: "Helencia",       description: "Statut Premium, niveau, badges décernés" },
];
const SECTION_BY_ID = Object.fromEntries(SECTIONS_DEFINITION.map((s) => [s.id, s]));

export default function PortfolioCustomizer({ pprofile, onSaved, userId }) {
  const [preset, setPreset] = useState(pprofile?.theme_preset || "caramel");
  const [accent, setAccent] = useState(pprofile?.theme_accent || PRESETS[0].colors.accent);
  const [bg, setBg] = useState(pprofile?.theme_bg || PRESETS[0].colors.bg);
  const [dark, setDark] = useState(pprofile?.theme_dark || PRESETS[0].colors.dark);
  const [fontSerif, setFontSerif] = useState(pprofile?.theme_font_serif || "Cormorant Garamond");
  const [coverVideoUrl, setCoverVideoUrl] = useState(pprofile?.cover_video_url || "");
  const [order, setOrder] = useState(
    Array.isArray(pprofile?.theme_section_order) && pprofile.theme_section_order.length > 0
      ? pprofile.theme_section_order
      : SECTIONS_DEFINITION.map((s) => s.id)
  );
  const [saving, setSaving] = useState(false);

  // Apply preset when changed (unless custom)
  useEffect(() => {
    const p = PRESETS.find((x) => x.id === preset);
    if (p && p.colors) {
      setAccent(p.colors.accent);
      setBg(p.colors.bg);
      setDark(p.colors.dark);
    }
  }, [preset]);

  const move = (idx, dir) => {
    const next = [...order];
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    setOrder(next);
  };

  const resetToDefaults = () => {
    if (!window.confirm("Restaurer le thème caramel par défaut ?")) return;
    setPreset("caramel");
    setAccent(PRESETS[0].colors.accent);
    setBg(PRESETS[0].colors.bg);
    setDark(PRESETS[0].colors.dark);
    setFontSerif("Cormorant Garamond");
    setOrder(SECTIONS_DEFINITION.map((s) => s.id));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/portfolio-profile/me", {
        theme_preset: preset,
        theme_accent: accent,
        theme_bg: bg,
        theme_dark: dark,
        theme_font_serif: fontSerif,
        theme_section_order: order,
        cover_video_url: coverVideoUrl.trim() || null,
      });
      toast.success("Personnalisation enregistrée");
      onSaved?.(data);
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="portfolio-customizer">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="caption-label text-[#C68E59]">Personnalisation</p>
          <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Votre portfolio, votre identité</h2>
          <p className="text-sm text-[#666] mt-2 max-w-xl">
            Couleurs, typographie et ordre des sections — ajustez chaque détail pour refléter votre univers.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => window.open(`/talents/${userId}`, "_blank")}
          data-testid="customizer-preview-btn"
        >
          <ExternalLink className="w-4 h-4 mr-2" /> Aperçu en direct
        </Button>
      </div>

      {/* ============ COLORS ============ */}
      <section className="card-lux p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#FAF3EA] flex items-center justify-center">
            <Palette className="w-4 h-4 text-[#A26F3E]" />
          </div>
          <div>
            <p className="caption-label text-[#A26F3E]">Couleurs</p>
            <h3 className="font-serif text-xl text-[#1A1A1A]">Palette du portfolio</h3>
          </div>
        </div>

        {/* Presets */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                preset === p.id ? "border-[#1A1A1A] ring-2 ring-[#1A1A1A]/10" : "border-[#EADDC9] hover:border-[#C68E59]"
              }`}
              data-testid={`preset-${p.id}`}
            >
              {p.colors ? (
                <div className="flex h-16">
                  <div className="flex-1" style={{ background: p.colors.bg }} />
                  <div className="flex-1" style={{ background: p.colors.accent }} />
                  <div className="flex-1" style={{ background: p.colors.dark }} />
                </div>
              ) : (
                <div className="h-16 bg-gradient-to-br from-[#EADDC9] via-white to-[#FAF3EA] flex items-center justify-center text-[10px] tracking-widest uppercase text-[#1A1A1A]">
                  Custom
                </div>
              )}
              <div className="p-2 text-center">
                <p className="text-[11px] font-medium text-[#1A1A1A]">{p.label.split(" ")[0]}</p>
              </div>
              {preset === p.id && (
                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center">
                  <Check className="w-3 h-3" />
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Color inputs */}
        <div className="grid sm:grid-cols-3 gap-4">
          <ColorPicker label="Accent (caramel)" value={accent} onChange={(v) => { setAccent(v); setPreset("custom"); }} testid="color-accent" />
          <ColorPicker label="Fond clair"       value={bg}     onChange={(v) => { setBg(v); setPreset("custom"); }}    testid="color-bg" />
          <ColorPicker label="Fond sombre"      value={dark}   onChange={(v) => { setDark(v); setPreset("custom"); }}  testid="color-dark" />
        </div>

        <p className="text-[11px] text-[#8B8680] mt-4 leading-relaxed">
          L'accent colore les noms italiques, les sous-titres et le bouton principal. Le fond clair est utilisé pour les sections Univers/Collaborations, le fond sombre pour Hero/Portfolio/Contact.
        </p>
      </section>

      {/* ============ TYPOGRAPHY ============ */}
      <section className="card-lux p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#FAF3EA] flex items-center justify-center">
            <Type className="w-4 h-4 text-[#A26F3E]" />
          </div>
          <div>
            <p className="caption-label text-[#A26F3E]">Typographie</p>
            <h3 className="font-serif text-xl text-[#1A1A1A]">Famille serif pour les titres</h3>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {FONTS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFontSerif(f.value)}
              className={`text-left p-5 rounded-2xl border-2 transition-all duration-300 ${
                fontSerif === f.value ? "border-[#1A1A1A] bg-[#FAF3EA]" : "border-[#EADDC9] hover:border-[#C68E59] bg-white"
              }`}
              data-testid={`font-${f.value.replace(/ /g, "-")}`}
            >
              <p className="text-3xl text-[#1A1A1A] italic" style={{ fontFamily: `'${f.value}', serif` }}>
                Helencia
              </p>
              <p className="text-[11px] tracking-wider uppercase text-[#8B8680] mt-2">{f.label}</p>
            </button>
          ))}
        </div>
      </section>

      {/* ============ COVER VIDEO ============ */}
      <section className="card-lux p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#FAF3EA] flex items-center justify-center">
            <Film className="w-4 h-4 text-[#A26F3E]" />
          </div>
          <div>
            <p className="caption-label text-[#A26F3E]">Vidéo de couverture</p>
            <h3 className="font-serif text-xl text-[#1A1A1A]">Animez votre Hero</h3>
          </div>
        </div>
        <p className="text-sm text-[#666] mb-4">
          Une mini vidéo en boucle (MP4 ou WebM) jouée silencieusement derrière votre nom géant. Idéale : 2-5s, format vertical 9:16 ou 16:9, &lt; 3 Mo.
          Hébergez-la sur un CDN (par ex. Cloudflare R2, Cloudinary, Mux) et collez l'URL `.mp4` ici.
        </p>
        <Input
          value={coverVideoUrl}
          onChange={(e) => setCoverVideoUrl(e.target.value)}
          placeholder="https://cdn.example.com/cover.mp4"
          className="rounded-xl font-mono text-sm"
          data-testid="cover-video-url"
        />
        {coverVideoUrl && (
          <div className="mt-4 rounded-2xl overflow-hidden bg-black aspect-video max-w-md">
            <video
              src={coverVideoUrl}
              autoPlay muted loop playsInline
              className="w-full h-full object-cover"
              onError={() => toast.error("Vidéo introuvable — vérifiez l'URL")}
            />
          </div>
        )}
      </section>

      {/* ============ SECTION ORDER ============ */}
      <section className="card-lux p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#FAF3EA] flex items-center justify-center">
            <ListOrdered className="w-4 h-4 text-[#A26F3E]" />
          </div>
          <div>
            <p className="caption-label text-[#A26F3E]">Ordre des sections</p>
            <h3 className="font-serif text-xl text-[#1A1A1A]">Composez votre récit</h3>
          </div>
        </div>
        <p className="text-sm text-[#666] mb-5">
          Le Hero et le Contact sont fixes. Vous pouvez réorganiser les sections du milieu.
        </p>
        <ul className="space-y-2" data-testid="section-order-list">
          {order.map((sid, i) => {
            const s = SECTION_BY_ID[sid];
            if (!s) return null;
            return (
              <li
                key={sid}
                className="flex items-center gap-3 p-4 rounded-2xl border border-[#EADDC9] bg-white hover:bg-[#FAF3EA] transition-colors"
                data-testid={`section-row-${sid}`}
              >
                <span className="w-8 h-8 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center text-[11px] font-semibold tracking-wider">
                  {String(i + 2).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-[#1A1A1A]">{s.label}</p>
                  <p className="text-xs text-[#8B8680] truncate">{s.description}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="w-9 h-9 rounded-full hover:bg-[#FAF3EA] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Monter"
                  >
                    <ChevronUp className="w-4 h-4 text-[#1A1A1A]" />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === order.length - 1}
                    className="w-9 h-9 rounded-full hover:bg-[#FAF3EA] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Descendre"
                  >
                    <ChevronDown className="w-4 h-4 text-[#1A1A1A]" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* ============ ACTIONS ============ */}
      <div className="sticky bottom-4 sm:bottom-6 flex items-center justify-between gap-3 p-4 rounded-2xl bg-white/95 backdrop-blur border border-[#EADDC9] shadow-[0_24px_60px_-30px_rgba(110,75,42,0.35)]">
        <Button
          onClick={resetToDefaults}
          variant="outline"
          className="rounded-full"
          data-testid="customizer-reset-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-[#1A1A1A] hover:bg-[#3A3A3A] text-white px-6 h-11"
          data-testid="customizer-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer la personnalisation"}
        </Button>
      </div>
    </div>
  );
}

function ColorPicker({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="caption-label">{label}</Label>
      <div className="flex items-center gap-2 mt-2">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="w-12 h-11 rounded-xl border border-[#EADDC9] cursor-pointer p-1"
          data-testid={`${testid}-swatch`}
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="rounded-xl font-mono uppercase tracking-wider"
          placeholder="#000000"
          maxLength={9}
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}
